import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { FileConversionService } from '../services/file-conversion.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, HeaderComponent, FooterComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  @ViewChild('uploadBox') uploadBox!: ElementRef;
  isDragOver = false;
  selectedFile: File | null = null;
  dragOverClass = '';
  isLoading = false;

  constructor(
    private router: Router,
    private fileConversionService: FileConversionService,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    // Définir le titre de la page (56 caractères - optimal)
    this.title.setTitle('Convertisseur PDF en Word en ligne – Rapide et simple');
    
    // Définir les meta tags SEO (160 caractères - optimal)
    this.meta.updateTag({ name: 'description', content: 'Convertissez vos fichiers PDF en documents Word modifiables en quelques clics. Conversion rapide, 100 % en ligne, sécurisée, sans installation requise.' });
    this.meta.updateTag({ name: 'keywords', content: 'convertisseur PDF Word, PDF to Word, PDF en Word, transformer PDF Word, modifier PDF, conversion PDF Word en ligne, outil PDF Word gratuit, convertir PDF docx' });
    
    // Meta tags supplémentaires pour le SEO
    this.meta.updateTag({ property: 'og:title', content: 'Convertisseur PDF en Word en ligne – Rapide et simple' });
    this.meta.updateTag({ property: 'og:description', content: 'Convertissez vos fichiers PDF en documents Word modifiables en quelques clics. Conversion rapide, 100 % en ligne, sécurisée, sans installation requise.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ name: 'author', content: 'Interactive Lab' });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    this.dragOverClass = 'drag-over';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.dragOverClass = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.dragOverClass = '';

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.handleFileSelection(file);
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    // Vérifier si le fichier est un PDF
    if (file.type === 'application/pdf') {
      this.selectedFile = file;
      console.log('Fichier PDF sélectionné:', file.name);
      // Ici vous pouvez ajouter la logique pour traiter le fichier
      // Par exemple, l'envoyer à un service de conversion
    } else {
      alert('Veuillez sélectionner un fichier PDF valide.');
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  convertToWord(): void {
    if (this.selectedFile) {
      this.isLoading = true;
      
      // Calculer la taille en MB
      const sizeInMB = parseFloat((this.selectedFile.size / 1024 / 1024).toFixed(2));
      
      // Stocker les données dans le service
      this.fileConversionService.setConversionData({
        filename: this.selectedFile.name,
        size: sizeInMB,
        originalFormat: '.pdf',
        targetFormat: '.docx',
        file: this.selectedFile // Ajouter le fichier uploadé
      });

      this.router.navigate(['/progress-page']);
    }
  }

  scrollToUpload(): void {
    const heroSection = document.getElementById('hero-section');
    if (heroSection) {
      const headerOffset = 80; // Offset pour tenir compte du header
      const elementPosition = heroSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Attendre que le scroll soit terminé avant de déclencher l'animation
      setTimeout(() => {
        this.triggerUploadAnimation();
      }, 600); // Délai pour laisser le temps au scroll de se terminer
    }
  }

  triggerUploadAnimation(): void {
    if (this.uploadBox?.nativeElement) {
      const uploadBoxElement = this.uploadBox.nativeElement;
      
      // Ajouter la classe d'animation
      uploadBoxElement.classList.add('attention-animation');
      
      // Retirer la classe après l'animation pour pouvoir la réutiliser
      setTimeout(() => {
        uploadBoxElement.classList.remove('attention-animation');
      }, 1500); // Durée de l'animation
    }
  }
}
